/**
 * Finite composition definitions (#1466 / ADR F-30).
 *
 * A definition may select a shipped recipe, bind its named slots to opaque
 * source keys, and place actions from the shared action vocabulary. It cannot
 * name a React component, URL, SQL function or executable handler. Hosts own
 * the source/action catalogs and populate them from ordinary governed doors.
 * Resolving a definition therefore composes authority; it never creates it.
 */

import { isRecord as object, unknownKeys } from './documentShape.js'

export const WORK_ACTION_KEYS = [
  'complete', 'assign', 'defer', 'request', 'approve', 'reject',
  'request_changes', 'resolve', 'respond', 'reopen', 'cancel',
] as const
export type WorkActionKey = typeof WORK_ACTION_KEYS[number]

export const EXPERIENCE_RECIPE_VERSION = 1 as const

export const RECIPE_GRAMMAR = {
  page_shell: {
    slots: ['identity', 'summary', 'status', 'secondary_actions', 'primary_action', 'content'],
    required: ['identity', 'content'],
    actionSlots: ['primary_action', 'secondary_actions'],
  },
  work_home: {
    slots: [
      'identity', 'summary', 'status', 'secondary_actions', 'primary_action',
      'attention', 'pinned', 'recent', 'secondary',
    ],
    required: ['identity', 'attention'],
    actionSlots: ['primary_action', 'secondary_actions'],
  },
  workspace_split: {
    slots: ['primary', 'inspector'],
    required: ['primary'],
    actionSlots: [],
  },
  journey_shell: {
    slots: [
      'identity', 'summary', 'status', 'progress', 'current_step',
      'blocker', 'next_step', 'recovery', 'content',
    ],
    required: ['identity', 'progress', 'current_step'],
    actionSlots: ['next_step', 'recovery'],
  },
  work_context: {
    slots: ['identity', 'why_now', 'evidence', 'recommendation', 'actions', 'receipt'],
    required: ['identity', 'evidence', 'actions'],
    actionSlots: ['actions'],
  },
} as const

export type ExperienceRecipeKey = keyof typeof RECIPE_GRAMMAR
export type ExperienceSlotKey = typeof RECIPE_GRAMMAR[ExperienceRecipeKey]['slots'][number]

export interface ExperienceSlotDefinition {
  key: ExperienceSlotKey
  /** Opaque catalog key. It is deliberately not a URL, SQL name or component. */
  source: string
}

export interface ExperienceActionDefinition {
  key: WorkActionKey
  slot: ExperienceSlotKey
  /** Opaque catalog key whose runtime binding carries the governed state. */
  binding: string
}

export interface ExperienceRecipeDefinition {
  schemaVersion: typeof EXPERIENCE_RECIPE_VERSION
  /** Stable, namespaced definition key (for example `service.case-work`). */
  key: string
  /** Monotonic stored-definition revision, independent of package versions. */
  revision: number
  recipe: ExperienceRecipeKey
  slots: ExperienceSlotDefinition[]
  actions?: ExperienceActionDefinition[]
}

export type GovernedSourceBinding =
  | { state: 'ready'; value: unknown }
  | { state: 'empty' }
  | { state: 'refused'; reason: string }

export type GovernedActionBinding =
  | { state: 'available'; action: WorkActionKey; value?: unknown }
  | { state: 'refused'; action: WorkActionKey; reason: string }

export interface ExperienceRecipeCatalog {
  sources?: Readonly<Record<string, GovernedSourceBinding>>
  actions?: Readonly<Record<string, GovernedActionBinding>>
}

export type ResolvedSourceBinding = GovernedSourceBinding | {
  state: 'unresolved'
  reason: 'source_not_bound' | 'source_binding_invalid'
}
export type ResolvedActionBinding = GovernedActionBinding | {
  state: 'unresolved'
  action: WorkActionKey
  reason: 'action_not_bound' | 'action_binding_invalid' | 'action_binding_mismatch'
}

export interface ResolvedExperienceSlot extends ExperienceSlotDefinition {
  binding: ResolvedSourceBinding
}
export interface ResolvedExperienceAction extends ExperienceActionDefinition {
  bindingState: ResolvedActionBinding
}
export interface ResolvedExperienceRecipe {
  schemaVersion: typeof EXPERIENCE_RECIPE_VERSION
  key: string
  revision: number
  recipe: ExperienceRecipeKey
  /** Always emitted in the recipe's canonical slot order. */
  slots: ResolvedExperienceSlot[]
  /** Definition order is intentional presentation order. */
  actions: ResolvedExperienceAction[]
  complete: boolean
}

const ACTION_KEYS = new Set<string>(WORK_ACTION_KEYS)
const DEFINITION_KEYS = new Set(['schemaVersion', 'key', 'revision', 'recipe', 'slots', 'actions'])
const SLOT_KEYS = new Set(['key', 'source'])
const ACTION_DEF_KEYS = new Set(['key', 'slot', 'binding'])
const IDENTIFIER = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/

export class ExperienceRecipeDefinitionError extends Error {
  constructor(readonly issues: string[]) {
    super(`Invalid experience recipe definition: ${issues.join('; ')}`)
    this.name = 'ExperienceRecipeDefinitionError'
  }
}

const validSourceBinding = (value: unknown): value is GovernedSourceBinding => {
  if (!object(value) || typeof value.state !== 'string') return false
  if (value.state === 'ready') return Object.hasOwn(value, 'value')
  if (value.state === 'empty') return true
  return value.state === 'refused' && typeof value.reason === 'string' && value.reason.trim().length > 0
}

const validActionBinding = (value: unknown): value is GovernedActionBinding => {
  if (!object(value) || !ACTION_KEYS.has(String(value.action))) return false
  if (value.state === 'available') return true
  return value.state === 'refused' && typeof value.reason === 'string' && value.reason.trim().length > 0
}

/** Runtime validation for definitions loaded from JSON or proposed by AI. */
export function validateExperienceRecipeDefinition(value: unknown): string[] {
  if (!object(value)) return ['definition must be an object']
  const issues = unknownKeys(value, DEFINITION_KEYS, 'definition')
  if (value.schemaVersion !== EXPERIENCE_RECIPE_VERSION) issues.push(`schemaVersion must be ${EXPERIENCE_RECIPE_VERSION}`)
  if (typeof value.key !== 'string' || !IDENTIFIER.test(value.key)) issues.push('key must be a namespaced identifier')
  if (!Number.isSafeInteger(value.revision) || Number(value.revision) < 1) issues.push('revision must be a positive integer')
  if (typeof value.recipe !== 'string' || !(value.recipe in RECIPE_GRAMMAR)) {
    issues.push('recipe is not shipped by this grammar version')
    return issues
  }

  const grammar = RECIPE_GRAMMAR[value.recipe as ExperienceRecipeKey]
  const allowedSlots = new Set<string>(grammar.slots)
  const actionSlots = new Set<string>(grammar.actionSlots)
  const seenSlots = new Set<string>()
  if (!Array.isArray(value.slots)) issues.push('slots must be an array')
  else value.slots.forEach((slot, index) => {
    if (!object(slot)) { issues.push(`slots[${index}] must be an object`); return }
    issues.push(...unknownKeys(slot, SLOT_KEYS, `slots[${index}]`))
    if (typeof slot.key !== 'string' || !allowedSlots.has(slot.key)) issues.push(`slots[${index}].key is not valid for ${value.recipe}`)
    else if (seenSlots.has(slot.key)) issues.push(`slot ${slot.key} is duplicated`)
    else seenSlots.add(slot.key)
    if (typeof slot.source !== 'string' || !IDENTIFIER.test(slot.source)) issues.push(`slots[${index}].source must be an opaque identifier`)
  })
  for (const required of grammar.required) if (!seenSlots.has(required)) issues.push(`required slot ${required} is missing`)

  const seenActions = new Set<string>()
  if (value.actions !== undefined && !Array.isArray(value.actions)) issues.push('actions must be an array')
  else if (Array.isArray(value.actions)) value.actions.forEach((action, index) => {
    if (!object(action)) { issues.push(`actions[${index}] must be an object`); return }
    issues.push(...unknownKeys(action, ACTION_DEF_KEYS, `actions[${index}]`))
    if (typeof action.key !== 'string' || !ACTION_KEYS.has(action.key)) issues.push(`actions[${index}].key is outside the shared action vocabulary`)
    if (typeof action.slot !== 'string' || !actionSlots.has(action.slot)) issues.push(`actions[${index}].slot cannot hold actions for ${value.recipe}`)
    if (typeof action.binding !== 'string' || !IDENTIFIER.test(action.binding)) issues.push(`actions[${index}].binding must be an opaque identifier`)
    const identity = `${String(action.slot)}:${String(action.key)}`
    if (seenActions.has(identity)) issues.push(`action ${identity} is duplicated`)
    else seenActions.add(identity)
  })
  return issues
}

/**
 * Bind a validated stored definition to runtime values. Missing or mismatched
 * catalog entries remain explicit unresolved states; they never become an
 * enabled action or silently disappear.
 */
export function resolveExperienceRecipe(value: unknown, catalog: ExperienceRecipeCatalog = {}): ResolvedExperienceRecipe {
  const issues = validateExperienceRecipeDefinition(value)
  if (issues.length) throw new ExperienceRecipeDefinitionError(issues)
  const definition = value as ExperienceRecipeDefinition
  const grammar = RECIPE_GRAMMAR[definition.recipe]
  const bySlot = new Map(definition.slots.map((slot) => [slot.key, slot]))
  const slots = grammar.slots.flatMap((key) => {
    const slot = bySlot.get(key)
    if (!slot) return []
    const bound: unknown = catalog.sources?.[slot.source]
    const binding: ResolvedSourceBinding = bound === undefined
      ? { state: 'unresolved', reason: 'source_not_bound' }
      : validSourceBinding(bound)
        ? bound
        : { state: 'unresolved', reason: 'source_binding_invalid' }
    return [{ ...slot, binding }]
  })
  const actions = (definition.actions ?? []).map((action) => {
    const bound: unknown = catalog.actions?.[action.binding]
    const bindingState: ResolvedActionBinding = bound === undefined
      ? { state: 'unresolved', action: action.key, reason: 'action_not_bound' }
      : !validActionBinding(bound)
        ? { state: 'unresolved', action: action.key, reason: 'action_binding_invalid' }
        : bound.action !== action.key
        ? { state: 'unresolved', action: action.key, reason: 'action_binding_mismatch' }
        : bound
    return { ...action, bindingState }
  })
  return {
    schemaVersion: definition.schemaVersion,
    key: definition.key,
    revision: definition.revision,
    recipe: definition.recipe,
    slots,
    actions,
    complete: slots.every((slot) => slot.binding.state !== 'unresolved')
      && actions.every((action) => action.bindingState.state !== 'unresolved'),
  }
}
